import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Users, BarChart3, FileCheck, MessageSquare, UserCheck, AlertCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${""}/api`;

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [ambassadorApps, setAmbassadorApps] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appFilter, setAppFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [systemErrors, setSystemErrors] = useState([]);

  const getAuthHeaders = () => {
    try {
      const stored = localStorage.getItem('pp_auth');
      if (stored) {
        const data = JSON.parse(stored);
        if (data.token) return { Authorization: `Bearer ${data.token}` };
      }
    } catch {}
    return {};
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { headers: getAuthHeaders() });
      toast.success(`Role updated to ${newRole}`);
      setEditingRole(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update role'); }
  };

  const handleDisableUser = async (userId, isDisabled) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/${isDisabled ? 'enable' : 'disable'}`, {}, { headers: getAuthHeaders() });
      toast.success(isDisabled ? 'Account enabled' : 'Account disabled');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      toast.success('Account deleted');
      setDeleteConfirm(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };


  useEffect(() => {
    fetchAll();
  }, []);

  // Fetch ALL users by paging through the API until every record is loaded.
  // The backend defaults to 50 per page, so a single un-paginated call only
  // ever returned the first 50 users even when more had signed up.
  const fetchAllUsers = async (headers) => {
    const pageSize = 200;
    let page = 1;
    let all = [];
    let total = Infinity;
    while (all.length < total) {
      const res = await axios.get(`${API}/admin/users?page=${page}&limit=${pageSize}`, { headers });
      const batch = res.data.users || [];
      total = typeof res.data.total === 'number' ? res.data.total : batch.length;
      all = all.concat(batch);
      if (batch.length === 0) break; // safety: stop if a page returns nothing
      page += 1;
      if (page > 100) break; // hard safety cap (20k users)
    }
    return all;
  };

  const fetchAll = async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    try {
      const [analyticsRes, usersRes, appsRes, inqRes, priceRes, ambRes, errRes] = await Promise.allSettled([
        axios.get(`${API}/admin/analytics`, { headers }),
        fetchAllUsers(headers),
        axios.get(`${API}/admin/applications`, { headers }),
        axios.get(`${API}/admin/patient-inquiries`, { headers }),
        axios.get(`${API}/admin/peptide-pricing`, { headers }),
        axios.get(`${API}/admin/ambassador-applications`, { headers }),
        axios.get(`${API}/admin/errors?limit=50`, { headers }),
      ]);
      if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data.analytics);
      // fetchAllUsers resolves directly to the user array (not an axios response).
      if (usersRes.status === 'fulfilled') setUsers(Array.isArray(usersRes.value) ? usersRes.value : []);
      if (appsRes.status === 'fulfilled') setApplications(appsRes.value.data.applications || []);
      if (inqRes.status === 'fulfilled') setInquiries(inqRes.value.data.inquiries || []);
      if (priceRes.status === 'fulfilled') setPricing(priceRes.value.data);
      if (ambRes.status === 'fulfilled') setAmbassadorApps(ambRes.value.data.applications || []);
      if (errRes.status === 'fulfilled') setSystemErrors(errRes.value.data.errors || []);
    } catch { toast.error('Failed to load admin data'); }
    setLoading(false);
  };

  const handleApprove = async (appId, type = 'contributing') => {
    const headers = getAuthHeaders();
    try {
      const endpoint = type === 'ambassador'
        ? `${API}/admin/ambassador-applications/${appId}/approve`
        : `${API}/admin/applications/${appId}/approve`;
      await axios.post(endpoint, {}, { headers });
      toast.success(`${type === 'ambassador' ? 'Ambassador' : 'Application'} approved — email sent`);
      fetchAll();
    } catch { toast.error('Failed to approve'); }
  };

  const handleDecline = async (appId, type = 'contributing') => {
    const headers = getAuthHeaders();
    try {
      const endpoint = type === 'ambassador'
        ? `${API}/admin/ambassador-applications/${appId}/decline`
        : `${API}/admin/applications/${appId}/decline`;
      await axios.post(endpoint, {}, { headers });
      toast.success('Application declined — email sent');
      fetchAll();
    } catch { toast.error('Failed to decline'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading admin dashboard...</div>;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-slate-800">Admin Dashboard</h1>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: analytics.total_users, icon: Users, color: 'text-blue-600' },
            { label: 'Active Subscribers', value: analytics.active_subscribers, icon: DollarSign, color: 'text-emerald-600' },
            { label: 'Protocols Generated', value: analytics.protocols_generated, icon: FileCheck, color: 'text-teal-600' },
            { label: 'Pending Applications', value: analytics.pending_applications, icon: UserCheck, color: 'text-amber-600' },
            { label: 'Community Posts', value: analytics.community_posts, icon: MessageSquare, color: 'text-indigo-600' },
            { label: 'Patient Inquiries', value: analytics.patient_inquiries, icon: AlertCircle, color: 'text-rose-600' },
            { label: 'Recent Signups (30d)', value: analytics.recent_registrations_30d, icon: BarChart3, color: 'text-purple-600' },
          ].map((stat, i) => (
            <Card key={i} className="border border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="users" className="w-full">
        {/* Mobile fix (2026-06-23): grid-cols-5 was wrong — 6 tabs caused the 6th
            (Errors) to wrap below, overlapping the User Management content. Now
            uses 2 cols on mobile, 3 on small, 6 on large. Tab labels are also
            smaller on narrow screens to prevent overlap. */}
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mb-4 h-auto gap-1">
          <TabsTrigger value="users" data-testid="admin-tab-users" className="text-xs sm:text-sm py-2">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="applications" data-testid="admin-tab-apps" className="text-xs sm:text-sm py-2">Apps ({applications.length})</TabsTrigger>
          <TabsTrigger value="ambassadors" data-testid="admin-tab-ambassadors" className="text-xs sm:text-sm py-2">Ambs ({ambassadorApps.length})</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries" className="text-xs sm:text-sm py-2">Inquiries</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="admin-tab-pricing" className="text-xs sm:text-sm py-2">Pricing</TabsTrigger>
          <TabsTrigger value="errors" data-testid="admin-tab-errors" className="text-xs sm:text-sm py-2">Errors ({systemErrors.length})</TabsTrigger>
        </TabsList>

        {/* ── Users Tab ── */}
        <TabsContent value="users">
          <Card>
            <CardHeader><CardTitle className="text-lg">User Management ({users.length} total)</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2 font-medium text-slate-600">Email</th>
                      <th className="text-left p-2 font-medium text-slate-600">Name</th>
                      <th className="text-left p-2 font-medium text-slate-600">Role</th>
                      <th className="text-left p-2 font-medium text-slate-600">Tier</th>
                      <th className="text-left p-2 font-medium text-slate-600">Signup Path</th>
                      <th className="text-left p-2 font-medium text-slate-600">Signup Date</th>
                      <th className="text-left p-2 font-medium text-slate-600">Protocols</th>
                      <th className="text-left p-2 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-user-row-${i}`}>
                        <td className="p-2 text-slate-800 text-xs">{u.email}</td>
                        <td className="p-2 text-slate-700 text-xs">{u.full_name || u.name || '—'}</td>
                        <td className="p-2">
                          <Badge variant="outline" className={`text-xs ${u.role === 'admin' ? 'border-red-300 text-red-700' : u.role === 'contributing' ? 'border-emerald-300 text-emerald-700' : ''}`}>
                            {u.role || 'practitioner'}
                          </Badge>
                        </td>
                        <td className="p-2 text-slate-600 text-xs">{u.subscription_tier || u.signup_path || 'pre_release'}</td>
                        <td className="p-2 text-xs">
                          <Badge variant="secondary" className="text-[10px]">
                            {u.signup_path || u.access_type || (u.access_code ? 'code' : 'direct')}
                          </Badge>
                        </td>
                        <td className="p-2 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                        <td className="p-2 text-slate-600 text-xs">{u.protocols_generated || 0}</td>
                        <td className="p-2">
                          <div className="flex gap-1 items-center">
                            {editingRole === (u.id || u.email) ? (
                              <select className="text-xs border rounded px-1 py-0.5" defaultValue={u.role || 'practitioner'}
                                onChange={(e) => handleRoleChange(u.id || u.email, e.target.value)}
                                onBlur={() => setEditingRole(null)}>
                                <option value="practitioner">Practitioner</option>
                                <option value="contributing">Contributing</option>
                                <option value="admin">Admin</option>
                              </select>
                            ) : (
                              <button onClick={() => setEditingRole(u.id || u.email)} className="text-[10px] text-blue-600 hover:text-blue-800" title="Edit Role">Role</button>
                            )}
                            <button onClick={() => handleDisableUser(u.id || u.email, u.disabled)}
                              className={`text-[10px] ${u.disabled ? 'text-green-600 hover:text-green-800' : 'text-amber-600 hover:text-amber-800'}`}
                              title={u.disabled ? 'Enable' : 'Disable'}>
                              {u.disabled ? 'Enable' : 'Disable'}
                            </button>
                            {u.email !== 'admin@peptideprotocols.ai' && (
                              <button onClick={() => setDeleteConfirm(u.id || u.email)}
                                className="text-[10px] text-red-600 hover:text-red-800" title="Delete">Del</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Contributing Practitioner Applications Tab ── */}
        <TabsContent value="applications">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Contributing Practitioner Applications</CardTitle>
                <div className="flex gap-1">
                  {['', 'pending', 'approved', 'declined'].map(f => (
                    <Button key={f} size="sm" variant={appFilter === f ? 'default' : 'outline'}
                      onClick={() => setAppFilter(f)} className="text-xs h-7">
                      {f || 'All'}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {applications.filter(a => !appFilter || a.status === appFilter).length === 0 ? (
                <p className="text-slate-400 text-center py-6">No applications match this filter.</p>
              ) : applications.filter(a => !appFilter || a.status === appFilter).map((app, i) => (
                <div key={i} className="p-4 border border-slate-200 rounded-lg space-y-2" data-testid={`admin-app-${i}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{app.full_name || app.name}</p>
                      <p className="text-xs text-slate-500">{app.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={app.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : app.status === 'declined' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                        {app.status || 'pending'}
                      </Badge>
                      {(!app.status || app.status === 'pending') && (
                        <>
                          <Button size="sm" onClick={() => handleApprove(app.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7" data-testid={`approve-app-${i}`}>Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => handleDecline(app.id)} className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50" data-testid={`decline-app-${i}`}>Reject</Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-slate-600 bg-slate-50 p-2 rounded">
                    <div><span className="font-medium text-slate-500">Credentials:</span> {app.credentials || '—'}</div>
                    <div><span className="font-medium text-slate-500">Specialty:</span> {app.specialty || '—'}</div>
                    <div><span className="font-medium text-slate-500">NPI:</span> {app.npi_number || '—'}</div>
                    <div><span className="font-medium text-slate-500">Experience:</span> {app.years_experience || '—'} yrs</div>
                    <div className="col-span-2"><span className="font-medium text-slate-500">Practice:</span> {app.practice_description || app.practice_name || '—'}</div>
                    <div><span className="font-medium text-slate-500">Contributions:</span> {(app.contribution_types || []).join(', ') || '—'}</div>
                    <div><span className="font-medium text-slate-500">Applied:</span> {app.created_at ? new Date(app.created_at).toLocaleDateString() : '—'}</div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Ambassador Applications Tab ── */}
        <TabsContent value="ambassadors">
          <Card>
            <CardHeader><CardTitle className="text-lg">Ambassador Applications</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {ambassadorApps.length === 0 ? (
                <p className="text-slate-400 text-center py-6">No ambassador applications yet.</p>
              ) : ambassadorApps.map((app, i) => (
                <div key={i} className="p-4 border border-slate-200 rounded-lg space-y-2" data-testid={`admin-amb-${i}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{app.full_name || app.name}</p>
                      <p className="text-xs text-slate-500">{app.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={app.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : app.status === 'declined' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                        {app.status || 'pending'}
                      </Badge>
                      {(!app.status || app.status === 'pending') && (
                        <>
                          <Button size="sm" onClick={() => handleApprove(app.id, 'ambassador')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7" data-testid={`approve-amb-${i}`}>Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => handleDecline(app.id, 'ambassador')} className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50" data-testid={`decline-amb-${i}`}>Reject</Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-slate-600 bg-slate-50 p-2 rounded">
                    <div><span className="font-medium text-slate-500">Tier:</span> {app.tier || '—'}</div>
                    <div><span className="font-medium text-slate-500">Website:</span> {app.website || '—'}</div>
                    <div><span className="font-medium text-slate-500">Approach:</span> {app.promotion_approach || app.how_promote || '—'}</div>
                    <div><span className="font-medium text-slate-500">Applied:</span> {app.created_at ? new Date(app.created_at).toLocaleDateString() : '—'}</div>
                    {app.referral_code && <div><span className="font-medium text-slate-500">Referral Code:</span> {app.referral_code}</div>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inquiries">
          <Card>
            <CardHeader><CardTitle className="text-lg">Patient Inquiries</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {inquiries.length === 0 ? (
                <p className="text-slate-400 text-center py-6">No patient inquiries yet.</p>
              ) : inquiries.map((inq, i) => (
                <div key={i} className="p-3 border border-slate-100 rounded-lg" data-testid={`admin-inquiry-${i}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-slate-800">{inq.name}</p>
                      <p className="text-xs text-slate-500">{inq.email} — {inq.location}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{inq.status || 'new'}</Badge>
                  </div>
                  {inq.health_concern && (
                    <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">{inq.health_concern}</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pricing">
          <Card>
            <CardHeader><CardTitle className="text-lg">DRSpeptides.com Pricing Reference</CardTitle></CardHeader>
            <CardContent>
              {pricing && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 font-medium text-slate-600">Peptide</th>
                        <th className="text-left p-2 font-medium text-slate-600">Monthly Cost</th>
                        <th className="text-left p-2 font-medium text-slate-600">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(pricing.drspeptides || {}).map(([name, p], i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="p-2 text-slate-800 font-medium">{name}</td>
                          <td className="p-2 text-slate-600">${p.monthly_cost_low}{p.monthly_cost_low !== p.monthly_cost_high ? `-$${p.monthly_cost_high}` : ''}/mo</td>
                          <td className="p-2"><Badge className="bg-teal-100 text-teal-700 text-xs">{p.source}</Badge></td>
                        </tr>
                      ))}
                      {Object.entries(pricing.market || {}).map(([name, p], i) => (
                        <tr key={`m-${i}`} className="border-t border-slate-100">
                          <td className="p-2 text-slate-800">{name}</td>
                          <td className="p-2 text-slate-600">${p.monthly_cost_low}-${p.monthly_cost_high}/mo</td>
                          <td className="p-2"><Badge variant="outline" className="text-xs">{p.source}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><AlertCircle className="h-5 w-5 text-red-500" /> System Errors ({systemErrors.length})</CardTitle></CardHeader>
            <CardContent>
              {systemErrors.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No errors logged</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 font-medium text-slate-600">Time</th>
                        <th className="text-left p-2 font-medium text-slate-600">Type</th>
                        <th className="text-left p-2 font-medium text-slate-600">Endpoint</th>
                        <th className="text-left p-2 font-medium text-slate-600">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemErrors.map((err, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="p-2 text-xs text-slate-500 whitespace-nowrap">{err.timestamp ? new Date(err.timestamp).toLocaleString() : '—'}</td>
                          <td className="p-2"><Badge variant="destructive" className="text-xs">{err.type}</Badge></td>
                          <td className="p-2 text-xs text-slate-600 font-mono">{err.endpoint || '—'}</td>
                          <td className="p-2 text-xs text-slate-700 max-w-md truncate">{err.details?.substring(0, 150)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-bold text-red-600 mb-2">Delete Account</h3>
            <p className="text-sm text-gray-600 mb-4">Are you sure? This cannot be undone. All user data will be permanently deleted.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDeleteUser(deleteConfirm)}>Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
